/**
 * iter-386u-bd — Searchable, cascading Main → Sub Category picker.
 * Backend endpoints: GET /boq-categories/main, GET /boq-categories/sub?main_category_id=…
 * Admins can override the AI's category guess in two clicks.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CategoryDropdown({
  mainId, mainName, subId, subName,
  onChange, // ({ main_category_id, main_category_name, sub_category_id, sub_category_name })
  size = 'sm',
  testIdPrefix = 'boq-category',
}) {
  const [mains, setMains] = useState([]);
  const [subs, setSubs] = useState([]);
  const [openWhich, setOpenWhich] = useState(null); // 'main' | 'sub' | null
  const [q, setQ] = useState('');
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    axios.get(`${API_ROOT}/boq-categories/main`, { headers })
      .then((r) => setMains(Array.isArray(r.data) ? r.data : []))
      .catch(() => setMains([]));
  }, []);

  useEffect(() => {
    if (!mainId) { setSubs([]); return; }
    axios.get(`${API_ROOT}/boq-categories/sub`, { headers, params: { main_category_id: mainId } })
      .then((r) => setSubs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSubs([]));
  }, [mainId]);

  const filteredMains = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return mains;
    return mains.filter((m) => (m.name || '').toLowerCase().includes(needle));
  }, [mains, q]);

  const filteredSubs = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return subs;
    return subs.filter((s) => (s.name || '').toLowerCase().includes(needle));
  }, [subs, q]);

  const btnClass = 'w-full text-left border border-stone-300 rounded bg-white px-2 py-1 text-xs hover:border-stone-400 flex items-center justify-between';

  const pickMain = (m) => {
    onChange && onChange({
      main_category_id: m.id, main_category_name: m.name,
      sub_category_id: null, sub_category_name: '',
    });
    setOpenWhich(null); setQ('');
  };
  const pickSub = (s) => {
    onChange && onChange({
      main_category_id: mainId, main_category_name: mainName,
      sub_category_id: s.id, sub_category_name: s.name,
    });
    setOpenWhich(null); setQ('');
  };
  const clearMain = (e) => {
    e.stopPropagation();
    onChange && onChange({ main_category_id: null, main_category_name: '', sub_category_id: null, sub_category_name: '' });
  };
  const clearSub = (e) => {
    e.stopPropagation();
    onChange && onChange({ main_category_id: mainId, main_category_name: mainName, sub_category_id: null, sub_category_name: '' });
  };

  const panelClass = 'absolute z-30 mt-1 w-full bg-white border border-stone-200 rounded shadow-lg max-h-56 overflow-y-auto';

  return (
    <div className="grid grid-cols-2 gap-2 text-xs">
      <div className="relative">
        <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-0.5">Main Category</div>
        <button type="button" className={btnClass}
          onClick={() => setOpenWhich((w) => w === 'main' ? null : 'main')}
          data-testid={`${testIdPrefix}-main-btn`}>
          <span className={mainName ? 'text-stone-900' : 'text-stone-400'}>{mainName || 'Pick main…'}</span>
          <span className="flex items-center gap-1">
            {mainName && <span onClick={clearMain} className="text-stone-400 hover:text-rose-500 text-[10px] leading-none">×</span>}
            <span className="text-stone-400 text-[10px]">▾</span>
          </span>
        </button>
        {openWhich === 'main' && (
          <div className={panelClass}>
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search main categories…"
              className="w-full px-2 py-1 border-b border-stone-100 text-xs outline-none"
              data-testid={`${testIdPrefix}-main-search`} />
            {filteredMains.length === 0 && <div className="p-3 text-[11px] text-stone-400 italic text-center">No matches</div>}
            {filteredMains.map((m) => (
              <button key={m.id} type="button"
                onClick={() => pickMain(m)}
                className={`w-full text-left px-2 py-1.5 hover:bg-emerald-50 flex items-center justify-between ${mainId === m.id ? 'bg-emerald-50 text-emerald-900 font-medium' : ''}`}
                data-testid={`${testIdPrefix}-main-opt-${m.id}`}>
                <span>{m.name}</span>
                {m.code && <span className="text-[9px] font-mono text-stone-400">{m.code}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="relative">
        <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-0.5">Sub Category</div>
        <button type="button" className={btnClass}
          disabled={!mainId}
          onClick={() => setOpenWhich((w) => w === 'sub' ? null : 'sub')}
          data-testid={`${testIdPrefix}-sub-btn`}>
          <span className={subName ? 'text-stone-900' : 'text-stone-400'}>
            {subName || (mainId ? 'Pick sub…' : 'Pick main first')}
          </span>
          <span className="flex items-center gap-1">
            {subName && <span onClick={clearSub} className="text-stone-400 hover:text-rose-500 text-[10px] leading-none">×</span>}
            <span className="text-stone-400 text-[10px]">▾</span>
          </span>
        </button>
        {openWhich === 'sub' && mainId && (
          <div className={panelClass}>
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search sub-categories…"
              className="w-full px-2 py-1 border-b border-stone-100 text-xs outline-none"
              data-testid={`${testIdPrefix}-sub-search`} />
            {filteredSubs.length === 0 && <div className="p-3 text-[11px] text-stone-400 italic text-center">No sub-categories in {mainName || 'this'}</div>}
            {filteredSubs.map((s) => (
              <button key={s.id} type="button"
                onClick={() => pickSub(s)}
                className={`w-full text-left px-2 py-1.5 hover:bg-emerald-50 flex items-center justify-between ${subId === s.id ? 'bg-emerald-50 text-emerald-900 font-medium' : ''}`}
                data-testid={`${testIdPrefix}-sub-opt-${s.id}`}>
                <span>{s.name}</span>
                {s.code && <span className="text-[9px] font-mono text-stone-400">{s.code}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
